import dayjs from "dayjs";
import { getDueDatePart } from "./dueDateTime";

function isTaskCompleted(status) {
  return status != null && String(status).trim() === "Closed";
}

/** True if the task has at least one time block (from getTask aggregate fields). */
export function taskHasTimeBlocks(task) {
  if (!task) return false;
  const n = Number(task.schedule_block_count);
  if (Number.isFinite(n) && n > 0) return true;
  const st = task.schedule_start_time;
  return st != null && String(st).trim() !== "";
}

/**
 * Schedule status for a task. Uses priority: Completed → Missed → Overdue → Behind → On Track → Ahead → Unscheduled.
 * @param {object} task – expects status, due_date, schedule_calendar_date (YYYY-MM-DD|null), has_passed_time_block (boolean)
 */
export function getTaskScheduleStatusLabel(task) {
  if (!task) return "Unscheduled";
  if (isTaskCompleted(task.status)) return "Completed";

  if (task.has_passed_time_block === true) return "Missed";

  const due = getDueDatePart(task.due_date);
  const today = dayjs().format("YYYY-MM-DD");
  if (due && today > due) return "Overdue";

  const schedRaw =
    task.schedule_calendar_date != null ? String(task.schedule_calendar_date).trim() : "";
  const sched = /^\d{4}-\d{2}-\d{2}$/.test(schedRaw) ? schedRaw : "";
  if (!sched || !due) return "Unscheduled";

  if (sched > due) return "Behind";
  if (sched === due) return "On Track";
  return "Ahead";
}

/** Stable stack / legend order (matches status priority). */
export const SCHEDULE_STATUS_ORDER = [
  "Completed",
  "Missed",
  "Overdue",
  "Behind",
  "On Track",
  "Ahead",
  "Unscheduled",
];

/**
 * Hex palette for charts and badges. Keys match {@link getTaskScheduleStatusLabel} output.
 * Completed / Missed / Unscheduled align with TimeBlocking.css border-left accents (vivid on charts).
 */
export const SCHEDULE_STATUS_COLORS = {
  Completed: "#22C55E", // Time Blocking: .tb-block--completed border-left
  Missed: "#F43F5E", // Time Blocking: .tb-block--missed border-left
  Overdue: "#F59E0B", // Burnt Amber — due date lapsed
  Behind: "#D97706", // Soft Ochre — plan risk, not yet late
  "On Track": "#0EA5E9", // Sky Cerulean — aligned
  Ahead: "#6366F1", // Indigo Violet — ahead of deadline
  Unscheduled: "#8b5cf6", // Time Blocking: unscheduled task cards border / accent
};

/**
 * Pill styles for schedule status (gradient badge, e.g. task table Schedule column).
 * @param {string} label – output of {@link getTaskScheduleStatusLabel}
 */
export function getScheduleStatusBadgeStyle(label) {
  const hex = SCHEDULE_STATUS_COLORS[label] || "#64748b";
  return {
    background: `linear-gradient(135deg, color-mix(in srgb, ${hex} 35%, white) 0%, ${hex} 48%, color-mix(in srgb, ${hex} 75%, black) 100%)`,
    color: "#fff",
    textShadow: "0 1px 1px rgba(0,0,0,0.2)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.12)",
  };
}

const LABEL_TO_PARAM = {
  Completed: "completed",
  Missed: "missed",
  Overdue: "overdue",
  Behind: "behind",
  "On Track": "on_track",
  Ahead: "ahead",
  Unscheduled: "unscheduled",
};

const PARAM_TO_LABEL = Object.fromEntries(
  Object.entries(LABEL_TO_PARAM).map(([k, v]) => [v, k])
);

/** URL query value for Task / Time Blocking drilldown. */
export function scheduleStatusLabelToParam(label) {
  return LABEL_TO_PARAM[label] || "unscheduled";
}

/** Decode URL param to exact schedule status label, or null if unknown. */
export function scheduleStatusParamToLabel(param) {
  if (param == null || String(param).trim() === "") return null;
  return PARAM_TO_LABEL[String(param).trim().toLowerCase()] || null;
}

/** Sort rank: lower = earlier in priority order (Completed first). */
export function getTaskScheduleStatusSortRank(label) {
  const orderMap = {
    completed: 0,
    missed: 1,
    overdue: 2,
    behind: 3,
    "on track": 4,
    ahead: 5,
    unscheduled: 6,
  };
  const key = (label || "").toLowerCase();
  return orderMap[key] ?? 99;
}
